/**
 * Live counters for the homepage — minutes moved and dollars raised across
 * the 911-Minute Legacy Challenge. Polls every 45s and counts up smoothly
 * so the numbers visibly climb while someone is reading.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function useCountUp(target, duration = 1100) {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target || 0);
    if (from === to) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

export default function LiveCounter() {
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/sacrifice/live-stats`);
        const j = await r.json();
        if (alive) setD(j);
      } catch { /* keep last known values */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const minutes = useCountUp(d?.total_minutes);
  const raised = useCountUp(d?.total_raised);
  const percent = Math.min(100, d?.percent_of_goal || 0);

  if (!d) return null;

  return (
    <section
      data-testid="live-counter"
      className="bg-[#0A0A0A] border-y border-white/10 px-6 md:px-12 py-12 sm:py-16"
    >
      <div className="max-w-5xl mx-auto">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-[10px] sm:text-xs font-bold mb-6">
          / live · 911-minute legacy challenge
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          <div data-testid="live-minutes">
            <div className="font-bold text-4xl sm:text-5xl leading-none tabular-nums">
              {Math.round(minutes).toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold mt-3">
              minutes moved
            </div>
          </div>
          <div data-testid="live-raised">
            <div className="font-bold text-4xl sm:text-5xl leading-none tabular-nums text-[#FF1B91]">
              ${Math.round(raised).toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold mt-3">
              raised for the families
            </div>
          </div>
          <div data-testid="live-participants">
            <div className="font-bold text-4xl sm:text-5xl leading-none tabular-nums">
              {Number(d.participants || 0).toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold mt-3">
              people moving
            </div>
          </div>
          <div data-testid="live-finishers">
            <div className="font-bold text-4xl sm:text-5xl leading-none tabular-nums">
              {Number(d.finishers || 0).toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold mt-3">
              finished all 911
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-baseline justify-between mb-2.5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold">
              collective goal
            </span>
            <span className="text-[11px] text-white/50 tabular-nums">
              {Number(d.total_minutes || 0).toLocaleString()} / {Number(d.collective_goal || 0).toLocaleString()} min · {d.percent_of_goal}%
            </span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#FF1B91] transition-[width] duration-1000 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <div className="mt-9 flex flex-wrap gap-3">
          <Link
            to="/legacy"
            data-testid="live-counter-join"
            className="inline-block bg-[#FF1B91] hover:bg-[#e01880] text-white font-bold uppercase tracking-[0.2em] text-[11px] px-7 py-3.5 transition-colors"
          >
            Join the challenge
          </Link>
          <Link
            to="/leaderboard"
            data-testid="live-counter-leaderboard"
            className="inline-block border border-white/25 hover:border-[#FF1B91] text-white font-bold uppercase tracking-[0.2em] text-[11px] px-7 py-3.5 transition-colors"
          >
            See the leaderboard
          </Link>
          {d.minutes_today > 0 && (
            <span className="self-center text-[11px] text-white/45">
              {Number(d.minutes_today).toLocaleString()} minutes logged today
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
